import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Eye, FileText, Loader2, PenLine, Upload, ShieldCheck, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { onboard } from '../api';
import { InfoForm } from '../components/prospects/InfoForm';
import { SignaturePad } from '../components/prospects/SignaturePad';
import { errMsg } from '../components/prospects/constants';

const STEPS = [['details', 'Your details', Building2], ['sign', 'Review & sign', PenLine], ['docs', 'Upload documents', Upload]];

const Shell = ({ data, token, children }) => (
  <div className="min-h-screen bg-[#f6f4ef]">
    <header className="bg-emerald-950 text-white">
      <div className="max-w-4xl mx-auto px-5 py-6 flex items-center gap-4">
        {data?.firm.hasLogo && <img src={onboard.logoUrl(token)} alt="" className="h-10 w-auto rounded bg-white p-1"/>}
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-300">Client onboarding</div>
          <div className="font-serif text-xl" data-testid="onboard-firm-name">{data?.firm.name || 'Loading…'}</div>
        </div>
      </div>
    </header>
    <main className="max-w-4xl mx-auto px-5 py-8">{children}</main>
    <footer className="max-w-4xl mx-auto px-5 pb-10 text-xs text-stone-500 flex items-center gap-1.5"><ShieldCheck size={13}/> Your information is protected under POPIA. Powered by Accu Desk.</footer>
  </div>
);

const SignStep = ({ data, token, onDone }) => {
  const pending = data.documents.filter((d) => d.status === 'sent');
  const [accepted, setAccepted] = useState([]);
  const [name, setName] = useState(data.prospect.contact || '');
  const [sig, setSig] = useState('');
  const [open, setOpen] = useState(pending[0]?.id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = () => {
    setBusy(true); setErr('');
    onboard.sign(token, { name, signature: sig, accepted }).then(onDone).catch((e) => setErr(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <div className="space-y-4" data-testid="onboard-sign-step">
      {data.documents.map((d) => (
        <div key={d.id} className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid={`onboard-doc-${d.id}`}>
          <button type="button" onClick={() => setOpen(open === d.id ? null : d.id)} className="w-full flex items-center gap-3 px-5 py-4 text-left">
            <FileText size={18} className="text-emerald-800"/>
            <span className="flex-1 font-medium text-stone-900">{d.title}</span>
            {d.status === 'signed' ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">Signed</span> : <span className="text-xs text-stone-500">{open === d.id ? 'Hide' : 'Read'}</span>}
          </button>
          {open === d.id && (
            <div className="px-5 pb-5">
              <div className="max-h-96 overflow-y-auto rounded-lg bg-stone-50 border border-stone-100 p-5 text-sm leading-relaxed text-stone-800 whitespace-pre-wrap" data-testid={`onboard-doc-text-${d.id}`}>{d.text.replace(/^#+ /gm, '')}</div>
              <a href={onboard.docPdfUrl(token, d.id)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-800 hover:underline"><Eye size={12}/> {d.status === 'signed' ? 'Download signed PDF' : 'View as PDF'}</a>
            </div>
          )}
          {d.status === 'sent' && (
            <label className="flex items-center gap-2.5 px-5 py-3 border-t border-stone-100 bg-emerald-50/50 text-sm cursor-pointer">
              <Checkbox checked={accepted.includes(d.id)} onCheckedChange={(c) => setAccepted(c ? [...accepted, d.id] : accepted.filter((x) => x !== d.id))} data-testid={`onboard-accept-${d.id}`}/>
              I have read and accept the {d.title}
            </label>
          )}
        </div>
      ))}
      {pending.length > 0 ? (
        <div className="rounded-xl bg-white border border-stone-200 p-5 space-y-4">
          <div>
            <Label htmlFor="sig-name" className="text-xs">Full name of person signing (authorised signatory)</Label>
            <Input id="sig-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11" data-testid="onboard-sign-name-input"/>
          </div>
          <SignaturePad onChange={setSig}/>
          <p className="text-xs text-stone-500">By signing you agree that your electronic signature is legally binding, as allowed by the Electronic Communications and Transactions Act 25 of 2002. The date, time and your IP address are recorded.</p>
          {err && <p className="text-sm text-red-700" data-testid="onboard-sign-error">{err}</p>}
          <Button disabled={busy || !sig || name.trim().length < 2 || accepted.length !== pending.length} onClick={submit} className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white text-base" data-testid="onboard-sign-btn">
            {busy && <Loader2 size={16} className="mr-2 animate-spin"/>} Sign {pending.length} document{pending.length > 1 ? 's' : ''}
          </Button>
        </div>
      ) : <p className="text-sm text-emerald-800 font-medium" data-testid="onboard-all-signed">All documents are signed. Thank you!</p>}
    </div>
  );
};

const DocsStep = ({ data, token, onDone }) => {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const refs = useRef({});
  const up = (id, f) => { setBusy(id); setErr(''); onboard.upload(token, id, f).then(onDone).catch((e) => setErr(errMsg(e))).finally(() => setBusy(null)); };
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="onboard-docs-step">
      <div className="px-5 py-4 border-b border-stone-100 text-sm text-stone-600">FICA requires us to verify every new client. Upload clear photos or PDFs. Certified copies must be dated within the last 3 months.</div>
      {err && <p className="px-5 pt-3 text-sm text-red-700">{err}</p>}
      <div className="divide-y divide-stone-100">
        {data.fica.map((it) => (
          <div key={it.id} className="flex items-center gap-3 px-5 py-3.5" data-testid={`onboard-fica-${it.id}`}>
            <span className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${it.status !== 'missing' ? 'bg-emerald-600 text-white' : 'border border-stone-300'}`}>{it.status !== 'missing' && <Check size={14}/>}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-stone-900">{it.label}{!it.required && <span className="ml-1.5 text-xs text-stone-400">optional</span>}</div>
              {it.fileName && <div className="text-xs text-stone-500 truncate">{it.fileName}</div>}
              {it.status === 'waived' && <div className="text-xs text-stone-500">Not needed</div>}
            </div>
            <input ref={(el) => { refs.current[it.id] = el; }} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && up(it.id, e.target.files[0])}/>
            {it.status !== 'waived' && (
              <Button size="sm" variant={it.status === 'received' ? 'outline' : 'default'} disabled={busy === it.id} onClick={() => refs.current[it.id].click()}
                className={it.status === 'received' ? '' : 'bg-emerald-800 hover:bg-emerald-900 text-white'} data-testid={`onboard-upload-${it.id}`}>
                {busy === it.id ? <Loader2 size={14} className="animate-spin"/> : <><Upload size={14} className="mr-1.5"/>{it.status === 'received' ? 'Replace' : 'Upload'}</>}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Onboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('details');
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const apply = (d) => { setData(d); setInfo(d.info); };
  useEffect(() => { onboard.get(token).then(apply).catch((e) => setError(errMsg(e))); }, [token]);

  if (error) return <Shell token={token}><div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-stone-700" data-testid="onboard-error">{error}</div></Shell>;
  if (!data) return <Shell token={token}><div className="p-16 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div></Shell>;

  const pr = data.progress;
  const done = { details: !!data.info?.registeredName, sign: pr.allSigned, docs: pr.ficaComplete };
  const saveInfo = () => {
    setSaving(true); setMsg('');
    onboard.saveInfo(token, info).then((d) => { apply(d); setMsg('Saved'); setStep('sign'); window.scrollTo({ top: 0, behavior: 'smooth' }); }).catch((e) => setMsg(errMsg(e))).finally(() => setSaving(false));
  };

  return (
    <Shell data={data} token={token}>
      <h1 className="font-serif text-3xl sm:text-4xl text-stone-900" data-testid="onboard-title">Welcome{data.prospect.contact ? `, ${data.prospect.contact.split(' ')[0]}` : ''}.</h1>
      <p className="mt-2 text-stone-600">Let's get <b>{data.prospect.name}</b> set up with {(data.firm.name || '').replace(/\.$/, '')}. It takes about 10 minutes. You can stop and come back to this link at any time.</p>
      {data.prospect.converted && <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-900 text-sm" data-testid="onboard-complete">Onboarding is complete. Welcome aboard! Your accountant will be in touch.</div>}
      {pr.allSigned && pr.ficaComplete && !data.prospect.converted && <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-900 text-sm" data-testid="onboard-all-done">Everything is in. Thank you! {data.firm.name} will confirm once your account is active.</div>}

      <div className="mt-8 grid grid-cols-3 gap-2" data-testid="onboard-steps">
        {STEPS.map(([k, l, Icon], i) => (
          <button key={k} onClick={() => setStep(k)} data-testid={`onboard-step-${k}`}
            className={`rounded-xl px-3 py-3 text-left border transition-colors ${step === k ? 'bg-white border-emerald-700 shadow-sm' : 'bg-white/60 border-stone-200 hover:bg-white'}`}>
            <div className="flex items-center gap-2">
              <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${done[k] ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-600'}`}>{done[k] ? <Check size={14}/> : i + 1}</span>
              <Icon size={15} className="text-stone-500 hidden sm:block"/>
            </div>
            <div className="mt-1.5 text-sm font-semibold text-stone-900">{l}</div>
            {k === 'sign' && <div className="text-xs text-stone-500">{pr.docsSigned}/{pr.docsSent} signed</div>}
            {k === 'docs' && <div className="text-xs text-stone-500">{pr.ficaDone}/{pr.ficaRequired} required</div>}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {step === 'details' && (
          <div className="rounded-xl bg-white border border-stone-200 p-5 sm:p-6">
            <InfoForm value={info} onChange={setInfo} needsDebit={data.needsDebit}/>
            <div className="mt-6 flex items-center justify-end gap-3">
              {msg && <span className="text-sm text-stone-600" data-testid="onboard-info-msg">{msg}</span>}
              <Button onClick={saveInfo} disabled={saving || data.prospect.converted} className="h-11 px-6 bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="onboard-info-save-btn">{saving && <Loader2 size={14} className="mr-2 animate-spin"/>} Save & continue</Button>
            </div>
          </div>
        )}
        {step === 'sign' && (data.documents.length ? <SignStep data={data} token={token} onDone={(d) => { apply(d); setStep('docs'); }}/> : <p className="text-sm text-stone-600" data-testid="onboard-no-docs">Your engagement documents are being prepared. Please check back soon.</p>)}
        {step === 'docs' && <DocsStep data={data} token={token} onDone={apply}/>}
      </div>
    </Shell>
  );
}
